import React, { ReactNode, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import styles from "./reportPage.module.scss";
import pl from "shared/messages/pl";
import { Loader } from "ecom-teams-apps-core";
import ReportTemplate from "shared/reports/components/reportTemplate/ReportTemplate";
import EmptyContent from "view360Card/components/emptyContent/EmptyContent";
import { ComponentEventHandler, FlexItem, Menu, MenuProps } from "@fluentui/react-northstar";
import { IMineTenantElements, IReport, IReportEntity, ITenantTableColumn } from "shared/reports/models/models";
import { IReportEntityType } from "shared/constants/reportConstants";
import { filter } from "lodash";

export interface ReportPageProps {
    report: IReport;
    renderCompanySectionElements: (report: IMineTenantElements) => ReactNode;
    renderTable: (
        entityType: IReportEntityType,
        entitiesForTable: IReportEntity[],
        tenantTableColumns: ITenantTableColumn[]
    ) => ReactNode;
    renderSummarySection: () => ReactNode | null;
    subtitleRow1?: string;
    subtitleRow2?: string;
}

export interface FilteringSettings {
    isFilteringInitialized: boolean;
    isFilteringByEntityTypeAvailable: boolean;
    activeReportEntityTypeIndex?: number;
}

const ReportPage = observer((props: ReportPageProps) => {
    const [filteringSettings, setFilteringSettings] = useState<FilteringSettings>({
        isFilteringInitialized: false,
        isFilteringByEntityTypeAvailable: false,
    });

    useEffect(() => {
        const defaultActive = props.report.entityTypeList.findIndex((i) => true);
        setFilteringSettings({
            isFilteringInitialized: true,
            activeReportEntityTypeIndex: defaultActive,
            isFilteringByEntityTypeAvailable: props.report.entityTypeList.length > 1,
        });
    }, [props.report]);

    const renderLoader = () => {
        return (
            <div className={styles.loaderWrapper}>
                <Loader text={pl.reports.loading} />
            </div>
        );
    };

    const renderMineSection = useMemo(() => {
        if (props.report.mineElementsByTenant.length === 0) {
            return <></>;
        }

        return (
            <>
                {props.report.mineElementsByTenant.map((mineElements) =>
                    props.renderCompanySectionElements(mineElements)
                )}
            </>
        );
    }, [props.report]);

    const handleOnActiveIndexChange: ComponentEventHandler<MenuProps> = (_, data) => {
        if (data?.activeIndex === undefined) {
            return;
        }
        const activeIndex = Number(data.activeIndex);

        setFilteringSettings({ ...filteringSettings, activeReportEntityTypeIndex: activeIndex });
    };

    const getTableFilteringSection = useMemo(() => {
        if (filteringSettings.isFilteringInitialized === false) {
            // Filtering not initialized yet
            return null;
        }
        if (filteringSettings.activeReportEntityTypeIndex === undefined) {
            return null;
        }

        if (filteringSettings.isFilteringByEntityTypeAvailable) {
            return (
                <FlexItem align="start">
                    <Menu
                        className={styles.filterMenu}
                        items={props.report.entityTypeList.map((i) => i.displayName)}
                        defaultActiveIndex={filteringSettings.activeReportEntityTypeIndex}
                        onActiveIndexChange={handleOnActiveIndexChange}
                    />
                </FlexItem>
            );
        }

        return null;
    }, [filteringSettings]);

    const renderTableSection = useMemo(() => {
        if (filteringSettings.isFilteringInitialized === false) {
            // Filtering not initialized yet
            return null;
        }
        if (filteringSettings.activeReportEntityTypeIndex === undefined) {
            return null;
        }

        const entitiesByType = props.report.entitiesByType[filteringSettings.activeReportEntityTypeIndex];
        const activeEntityType = entitiesByType.entityType;
        const entitiesForTable = entitiesByType.entities.filter((i) => i.mine !== true);

        if (entitiesForTable.length > 0) {
            return props.renderTable(activeEntityType, entitiesForTable, entitiesByType.tenantTableColumns);
        }
        return null;
    }, [filteringSettings]);

    const renderSummarySection = useMemo(() => {
        return props.renderSummarySection();
    }, [props.report]);

    if (filteringSettings.isFilteringInitialized === false) {
        renderLoader();
    }

    if (props.report.entitiesByType.length === 0) {
        return <EmptyContent message={pl.common.noData} graphic="welcome" />;
    }

    return (
        <ReportTemplate
            header={pl.reports.headers.companyReportsHeader}
            mineSection={renderMineSection}
            subtitle1={props.subtitleRow1}
            subtitle2={props.subtitleRow2}
            tableHeader={pl.reports.headers.tableHeader}
            tableFilteringSection={getTableFilteringSection}
            tableSection={renderTableSection}
            summarySection={renderSummarySection}
        />
    );
});

export default ReportPage;
